import { createFileRoute } from '@tanstack/react-router'
import { ExpeditionAssuranceFlow } from '../components/ExpeditionAssuranceFlow'
import { Footer, HeaderMiddle, Navbar, TopBar, WA_NUMBER } from '../components/SiteChrome'
import { getStorefrontConfig } from '../lib/dropify'
import { whatsAppCtaProps } from '../lib/analytics'
import { DEFAULT_OG_IMAGE, absoluteUrl, brandTitle, buildBreadcrumbSchema, buildFaqSchema } from '../lib/seo'

const PATH = '/jaminan-ekspedisi'
const SEO_TITLE = 'Verifikasi Pesanan di Ekspedisi Pilihan Sebelum Transfer'
const SEO_DESCRIPTION = 'Panduan pembeli baru: pilih ekspedisi yang dipercaya, terima resi setelah barang diserahkan, lalu cek pesanan langsung ke cabang sebelum transfer.'

export const EXPEDITION_ASSURANCE_WA_MESSAGE =
  'Halo Dropify, saya ingin memesan dengan verifikasi di ekspedisi pilihan sebelum transfer. Produk/model: ___; jumlah per model: ___; kota tujuan: ___; nama dan cabang ekspedisi: ___; kontak cabang: ___. Bisa jelaskan alur penyerahan barang dan pengecekan jenis serta jumlahnya?'

const WA_HREF = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(EXPEDITION_ASSURANCE_WA_MESSAGE)}`

const FAQ_ITEMS = [
  {
    q: 'Apakah saya boleh memilih ekspedisi langganan sendiri?',
    a: 'Boleh. Anda dapat mengusulkan cabang ekspedisi langganan di Surabaya, Bandung, atau Jakarta. Kirim nama ekspedisi, alamat cabang, nomor kontak, dan kota tujuan. Admin akan memastikan titik penyerahan barang yang dapat dilayani sebelum pesanan diproses.',
  },
  {
    q: 'Apakah nomor resi membuktikan isi paket sudah sesuai?',
    a: 'Resi menunjukkan paket diterima ekspedisi serta rincian yang dicatat petugas, seperti jumlah koli atau berat jika tersedia. Pengecekan jenis dan jumlah barang perlu Anda sepakati langsung dengan cabang sebelum memilih cara ini.',
  },
  {
    q: 'Bagaimana jika cabang hanya dapat memastikan jumlah koli?',
    a: 'Sampaikan batas pengecekan tersebut kepada admin sebelum pesanan disiapkan. Anda dapat memilih cabang lain yang bersedia mencocokkan barang, atau mendiskusikan dokumentasi pengemasan yang dibutuhkan.',
  },
  {
    q: 'Kapan saya perlu transfer?',
    a: 'Untuk alur ini, lakukan transfer setelah resi Anda terima dan hasil pengecekan melalui ekspedisi sesuai dengan daftar pesanan. Jika ada perbedaan, hubungi admin lebih dulu agar rinciannya diperiksa.',
  },
  {
    q: 'Apakah pembayaran dilakukan kepada kurir saat paket tiba?',
    a: 'Pembayaran tetap melalui transfer bank ke rekening yang dikonfirmasi admin Dropify. Kurir tidak menerima pembayaran saat paket tiba.',
  },
] as const

const CONTENT_LINKS = [
  { href: '#alur', label: 'Gambaran alur' },
  { href: '#sebelum-mulai', label: 'Sebelum barang disiapkan' },
  { href: '#fungsi-resi', label: 'Resi dan pengecekan isi' },
  { href: '#jika-berbeda', label: 'Jika ada perbedaan' },
  { href: '#pertanyaan', label: 'Pertanyaan umum' },
] as const

export const Route = createFileRoute('/jaminan-ekspedisi')({
  loader: async () => ({ config: await getStorefrontConfig().catch(() => null) }),
  head: () => {
    const title = brandTitle(SEO_TITLE)
    return {
      meta: [
        { title },
        { name: 'description', content: SEO_DESCRIPTION },
        { name: 'robots', content: 'index, follow' },
        { property: 'og:title', content: title },
        { property: 'og:description', content: SEO_DESCRIPTION },
        { property: 'og:url', content: absoluteUrl(PATH) },
        { property: 'og:image', content: DEFAULT_OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: SEO_DESCRIPTION },
        { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(PATH) }],
      scripts: [
        { type: 'application/ld+json', children: JSON.stringify(buildFaqSchema(FAQ_ITEMS.map(({ q, a }) => ({ q, a })))) },
        { type: 'application/ld+json', children: JSON.stringify(buildBreadcrumbSchema([{ name: 'Beranda', path: '/' }, { name: 'Jaminan ekspedisi', path: PATH }])) },
      ],
    }
  },
  component: ExpeditionAssurancePage,
})

function ExpeditionAssurancePage() {
  const { config } = Route.useLoaderData()

  return (
    <div className="min-h-screen bg-[#f8f7f3] text-[#17354a]">
      <TopBar config={config} />
      <HeaderMiddle />
      <Navbar />

      <main>
        <header className="border-b border-[#e6e5de] bg-[#fffdfa]">
          <div className="mx-auto max-w-6xl px-4 pb-10 pt-6 sm:pb-14 sm:pt-8">
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-slate-500">
              <a href="/" className="underline-offset-4 hover:underline">Beranda</a>
              <span aria-hidden="true">/</span>
              <span aria-current="page">Jaminan ekspedisi</span>
            </nav>
            <div className="mt-12 max-w-4xl sm:mt-16">
              <p className="mb-5 text-xs font-bold uppercase tracking-[0.2em] text-[#157d75]">Panduan untuk pembeli baru</p>
              <h1 className="max-w-3xl text-[2.55rem] font-black leading-[1.12] tracking-tight sm:text-[3.7rem]">
                Periksa pesanan di ekspedisi pilihan sebelum transfer
              </h1>
              <p className="mt-7 max-w-3xl text-lg leading-relaxed text-slate-700">
                Wajar jika Anda ingin memastikan pesanan pertama sebelum mengirim pembayaran. Anda dapat memilih ekspedisi yang sudah dikenal. Kami akan menyepakati titik penyerahan barang, mengemas pesanan, dan mengirim resinya setelah cabang menerima kiriman.
              </p>
              <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600">
                Dengan resi tersebut, Anda dapat menghubungi cabang untuk mencocokkan kiriman. Transfer dilakukan setelah hasil pengecekan sesuai dengan daftar pesanan.
              </p>
            </div>
          </div>
        </header>

        <section id="alur" aria-labelledby="alur-title" className="mx-auto max-w-6xl px-4 pb-12 pt-10 sm:pb-16 sm:pt-14">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#157d75]">Gambaran alur</p>
              <h2 id="alur-title" className="mt-2 text-3xl font-black leading-tight tracking-tight sm:text-4xl">Dari pilihan cabang sampai pembayaran</h2>
            </div>
            <p className="max-w-xs text-sm leading-relaxed text-slate-600">Cabang dan cara memeriksa isi kiriman disepakati sebelum barang kami siapkan.</p>
          </div>
          <ExpeditionAssuranceFlow />
        </section>

        <div className="mx-auto grid max-w-6xl gap-10 border-t border-[#e1e1d9] px-4 pb-20 pt-12 lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-16 lg:pt-16">
          <aside className="hidden lg:block">
            <nav aria-label="Isi panduan" className="sticky top-6 border-l border-[#d9ddd9] pl-5">
              <p className="mb-5 text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Isi panduan</p>
              <ul className="space-y-3">
                {CONTENT_LINKS.map(({ href, label }) => (
                  <li key={href}><a href={href} className="text-sm leading-snug text-slate-600 underline-offset-4 hover:text-[#126e65] hover:underline">{label}</a></li>
                ))}
              </ul>
            </nav>
          </aside>

          <article className="min-w-0 max-w-3xl">
            <section id="sebelum-mulai" aria-labelledby="sebelum-title" className="scroll-mt-8">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#157d75]">Sebelum barang disiapkan</p>
              <h2 id="sebelum-title" className="mt-3 text-3xl font-black leading-tight tracking-tight sm:text-4xl">Tentukan cabang dan cara pengecekannya</h2>
              <p className="mt-5 text-base leading-8 text-slate-700">
                Anda boleh mengusulkan ekspedisi langganan di Surabaya, Bandung, atau Jakarta. Beri tahu admin nama dan alamat cabangnya. Kami akan memastikan lokasi penyerahan yang dapat kami layani sebelum pesanan diproses.
              </p>
              <p className="mt-4 text-base leading-8 text-slate-700">
                Hubungi cabang pilihan Anda lebih dulu. Tanyakan apakah petugas bersedia mencocokkan jenis dan jumlah barang dengan daftar pesanan. Setiap cabang memiliki aturan penerimaan dan pengecekan sendiri.
              </p>
              <div className="mt-8 border-y border-[#dce1df] py-6 sm:py-7">
                <h3 className="text-sm font-semibold text-[#17354a]">Informasi yang perlu dikirim ke admin</h3>
                <ul className="mt-4 list-disc space-y-2 pl-5 text-sm leading-7 text-slate-700 marker:text-[#157d75]">
                  <li>Nama atau kode produk, varian, dan jumlah per model.</li>
                  <li>Kota tujuan dan perkiraan waktu barang diperlukan.</li>
                  <li>Nama ekspedisi, alamat serta kontak cabang, dan cara pengecekan yang disetujui petugas.</li>
                </ul>
              </div>
            </section>

            <section id="fungsi-resi" aria-labelledby="resi-title" className="scroll-mt-8 pt-16 sm:pt-20">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#157d75]">Memahami bukti kiriman</p>
              <h2 id="resi-title" className="mt-3 text-3xl font-black leading-tight tracking-tight sm:text-4xl">Apa yang dapat dipastikan dari resi?</h2>
              <p className="mt-5 text-base leading-8 text-slate-700">
                Resi membantu Anda mengonfirmasi bahwa kiriman sudah diterima cabang ekspedisi. Nomornya dapat ditanyakan langsung kepada petugas. Rincian pada resi mengikuti data yang dicatat petugas, misalnya jumlah koli dan berat.
              </p>
              <div className="mt-8 grid border-y border-[#dce1df] sm:grid-cols-2">
                <div className="py-6 pr-6 sm:border-r sm:border-[#dce1df] sm:py-7">
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#157d75]">Yang dapat dicocokkan</p>
                  <h3 className="mt-2 text-lg font-semibold">Penyerahan paket</h3>
                  <p className="mt-2 text-sm leading-7 text-slate-600">Nomor resi, status penerimaan, dan rincian kiriman yang tercatat di cabang.</p>
                </div>
                <div className="border-t border-[#dce1df] py-6 sm:border-t-0 sm:py-7 sm:pl-7">
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#a66a2b]">Perlu kesepakatan tambahan</p>
                  <h3 className="mt-2 text-lg font-semibold">Isi pesanan</h3>
                  <p className="mt-2 text-sm leading-7 text-slate-600">Jenis dan jumlah barang di dalam kemasan perlu diperiksa dengan cara yang disetujui petugas ekspedisi. Resi saja belum menjelaskan seluruh isi paket.</p>
                </div>
              </div>
            </section>

            <section id="jika-berbeda" aria-labelledby="berbeda-title" className="scroll-mt-8 pt-16 sm:pt-20">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#157d75]">Sebelum transfer</p>
              <h2 id="berbeda-title" className="mt-3 text-3xl font-black leading-tight tracking-tight sm:text-4xl">Jika ada perbedaan, beri tahu kami</h2>
              <p className="mt-5 text-base leading-8 text-slate-700">
                Jika jenis atau jumlah yang dikonfirmasi petugas ekspedisi berbeda dari daftar pesanan, tunda transfer. Kirim rincian perbedaannya kepada admin agar kami dapat mencocokkan pesanan, kemasan, dan catatan serah terima.
              </p>
              <p className="mt-4 text-base leading-8 text-slate-700">
                Jika cabang hanya dapat memastikan jumlah koli, sampaikan batas pengecekan itu kepada admin. Anda dapat memilih cabang lain atau mendiskusikan dokumentasi pengemasan yang dibutuhkan sebelum proses dilanjutkan.
              </p>
            </section>

            <section id="pertanyaan" aria-labelledby="faq-title" className="scroll-mt-8 pt-16 sm:pt-20">
              <h2 id="faq-title" className="text-3xl font-black leading-tight tracking-tight sm:text-4xl">Pertanyaan yang sering muncul</h2>
              <div className="mt-6 border-t border-[#dce1df]">
                {FAQ_ITEMS.map(({ q, a }) => (
                  <details key={q} className="group border-b border-[#dce1df] py-5">
                    <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold text-[#17354a] marker:hidden [&::-webkit-details-marker]:hidden">
                      <span>{q}</span>
                      <span aria-hidden="true" className="text-xl font-normal leading-5 text-[#157d75] group-open:rotate-45">+</span>
                    </summary>
                    <p className="max-w-2xl pt-3 text-sm leading-7 text-slate-700">{a}</p>
                  </details>
                ))}
              </div>
            </section>

            <div className="mt-16 border-t border-[#dce1df] pt-8 sm:mt-20">
              <h2 className="text-2xl font-black tracking-tight">Ingin membahas cabang pilihan Anda?</h2>
              <p className="mt-3 text-sm leading-7 text-slate-700">Kirim daftar barang, jumlah, kota tujuan, dan kontak cabang. Admin akan memeriksa stok serta cara penyerahan yang dapat dilakukan.</p>
              <a href={WA_HREF} {...whatsAppCtaProps('landing_page', { cta_placement: 'article_end', lp_category: 'Pemesanan grosir', lead_item_name: 'Verifikasi ekspedisi pilihan' })} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex min-h-11 items-center gap-2 border-b-2 border-[#157d75] pb-1 text-sm font-semibold text-[#126e65] hover:text-[#0b514b]">
                Tanyakan alur ini lewat WhatsApp <span aria-hidden="true">↗</span>
              </a>
              <p className="mt-9 text-xs leading-6 text-slate-500">Contoh penyiapan pesanan pelanggan baru: <a href="/berita/pesanan-pertama-1200-lusin-sarung-tangan-kaos-kaki-jakarta-timur" className="font-semibold text-[#126e65] underline underline-offset-2">1.200 lusin sarung tangan dan kaos kaki ke Jakarta Timur</a>.</p>
            </div>
          </article>
        </div>
      </main>

      <Footer config={config} />
    </div>
  )
}
